import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { HighPrioritySystemMessage } from '@zg-rentals/satellite-components';
import { useQueryClient } from '@zg-rentals/react-query5';
import type { ConversationMessage, ConversationMessagesData } from '../../../../utils/conversationMessageUtils';
import { useSatelliteWebLandlordApplicationHandler_inviteToApply } from '@zg-rentals/ts-satellite-client-react-query';
import { ModalType } from '../../../../types/ModalType';
import StringConstants from '../../../../constants/StringConstants';
import useModal from '../../../../hooks/useModal';
import { APPLICATIONS_UPSELL_MODAL_COOKIE } from '../../../Modals/ApplicationsUpsellModal';
import { appMarkerLogger } from '../../../../bootstrap';
import {
  patchConversationMessagesNote,
  useConversationMessagesQuery,
} from '../../../../hooks/useConversationMessagesQuery';

type Props = {
  conversationId: string;
  clientTimeZone: string;
  listingAlias: string;
  messageObject: ConversationMessage;
};

export type ApplicationRequestData = {
  isApplicationAlreadySent: boolean;
  isApplicationsAccepted: boolean;
  renterEmail: string;
  renterName: string;
};

export const selectApplicationRequestData = (data: ConversationMessagesData): ApplicationRequestData => ({
  isApplicationAlreadySent: data.note.isApplicationAlreadySent,
  isApplicationsAccepted: data.isApplicationsAccepted,
  renterEmail: data.renterEmail ?? '',
  renterName: data.renterName ?? '',
});

const logger = appMarkerLogger.child({ name: 'ApplicationRequest' });

const ApplicationRequest = ({
  conversationId,
  clientTimeZone,
  listingAlias,
  messageObject,
}: Props): JSX.Element | null => {
  let ctaText = '';
  const [isSendingApplicationRequest, setIsSendingApplicationRequest] = useState(false);
  const [cookies] = useCookies([APPLICATIONS_UPSELL_MODAL_COOKIE]);
  const { setActiveModal } = useModal();
  const queryClient = useQueryClient();

  const { messageLink, message, messageDateMs, messageType, title, uiTreatmentType } = messageObject;

  if (messageLink) {
    ctaText = messageLink.text;
  }

  // Cache-only read — this system message renders inside the message list, so
  // `MessageListItems` has already fetched getMessages for this conversation
  // (both the inbox and the lead-management RenterMessagingModal).
  const { data: conversationData } = useConversationMessagesQuery({
    conversationId,
    clientTimeZone,
    enabled: false,
    select: selectApplicationRequestData,
  });

  const { mutateAsync: inviteToApply } = useSatelliteWebLandlordApplicationHandler_inviteToApply({
    meta: {
      context: {
        api: 'inviteToApply',
        details: 'ApplicationRequest - invite renter to apply from system message',
      },
    },
  });

  if (!conversationData) {
    return null;
  }

  if (conversationData.isApplicationAlreadySent) {
    ctaText = StringConstants.SYSTEM_MESSAGE_ACTION.resendApplication;
  }

  const handleSendApplication = async (isRetry = false) => {
    setIsSendingApplicationRequest(true);
    const renterNameArray = conversationData.renterName.split(' ');
    const firstName = renterNameArray[0] || '';
    const lastName = renterNameArray.length > 1 ? renterNameArray[renterNameArray.length - 1] : '';
    const postData = {
      listingAlias: listingAlias || '',
      renterFirstName: firstName,
      renterLastName: lastName,
      toEmail: conversationData.renterEmail,
      resendEmail: true,
    };
    logger.info(postData, 'firing invite to apply from system message');
    try {
      await inviteToApply({ postData });

      await patchConversationMessagesNote(
        queryClient,
        { conversationId, clientTimeZone },
        { isApplicationAlreadySent: true },
      );

      if (!conversationData.isApplicationsAccepted && !cookies[APPLICATIONS_UPSELL_MODAL_COOKIE]) {
        setActiveModal(ModalType.ApplicationsUpsellModal);
      }
    } catch {
      if (!isRetry) {
        return handleSendApplication(true);
      }
    }
    setIsSendingApplicationRequest(false);
  };

  return (
    <HighPrioritySystemMessage
      ctaText={isSendingApplicationRequest ? 'Sending application...' : ctaText}
      message={message || ''}
      messageDateMs={messageDateMs}
      messageType={messageType}
      onCtaClick={handleSendApplication}
      title={title || ''}
      uiTreatmentType={uiTreatmentType || ''}
    />
  );
};

export default ApplicationRequest;
